import { useQuery } from "@tanstack/react-query";
import type { ModuleKey } from "@kipo/shared";
import type { IconName } from "../ui/Icon";
import { fmtTime, fullName } from "./format";
import { useCan, useModuleActive } from "./me";
import { useTRPC } from "./trpc";

export type Bucket = "late" | "today" | "next" | "none";
const ACT_ICON: Record<string, IconName> = { call: "phone", meeting: "users", email: "mail", task: "check-list", deadline: "clock" };
const ACT_LABEL: Record<string, string> = { call: "Chiamata", meeting: "Riunione", email: "Email", task: "Da fare", deadline: "Scadenza" };
export const romeDay = (d: Date) => d.toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

/** Una riga della lista unica: task, attività CRM o fattura da sollecitare. */
export interface TodoItem {
  key: string;
  bucket: Bucket;
  /** giorno di scadenza (YYYY-MM-DD, ora di Roma), vuoto se non c'è */
  day: string;
  /** solo l'ora, per le attività con orario */
  time?: string;
  sort: string;
  module: ModuleKey;
  icon: IconName;
  kind: string;
  title: string;
  link: { to: string; params?: Record<string, string>; search?: Record<string, string> };
  context?: { label: string; to: string; params: Record<string, string> };
  amount?: string;
  activityId?: string;
}

/** Le cose da fare dell'utente (task, attività CRM, fatture da sollecitare): usate dalla pagina e dal badge del menu. */
export function useTodos(enabled = true) {
  const trpc = useTRPC();
  const can = useCan();
  const active = useModuleActive();
  const showTasks = enabled && active("projects") && can("projects:read");
  const showActs = enabled && active("crm") && can("activities:read");
  const q = useQuery({ ...trpc.dashboard.overview.queryOptions(), enabled });
  const tasks = useQuery({ ...trpc.projects.tasks.mine.queryOptions({ includeClosed: false }), enabled: showTasks });
  const acts = useQuery({ ...trpc.crm.activities.list.queryOptions({ scope: "mine", status: "open" }), enabled: showActs });

  const now = new Date();
  const today = romeDay(now);
  const loading = q.isLoading || tasks.isLoading || acts.isLoading;
  const items: TodoItem[] = [
    ...(q.data?.agenda ?? [])
      .filter((a) => a.module === "sales")
      .map((a, i): TodoItem => {
        const [path, qs] = a.link.split("?");
        return { key: `inv-${i}`, bucket: "late", day: "", sort: "", module: "sales", icon: "receipt", kind: "Fattura scaduta da sollecitare", title: a.title.replace(/^Sollecito fattura /, "Fattura "), link: { to: path!, search: Object.fromEntries(new URLSearchParams(qs ?? "")) }, amount: a.meta ?? undefined };
      }),
    ...(tasks.data ?? []).map((t): TodoItem => {
      const bucket: Bucket = !t.endDate ? "none" : t.endDate < today ? "late" : t.endDate === today ? "today" : "next";
      return { key: `task-${t.id}`, bucket, day: t.endDate ?? "", sort: t.endDate ?? "", module: "projects", icon: t.isMilestone ? "rocket" : "kanban", kind: "Task", title: t.title, link: { to: "/projects/$projectId", params: { projectId: t.projectId }, search: { task: t.id } }, context: { label: t.project.name, to: "/projects/$projectId", params: { projectId: t.projectId } } };
    }),
    ...(acts.data ?? []).map((a): TodoItem => {
      const due = a.dueAt ? new Date(a.dueAt) : null;
      const bucket: Bucket = !due ? "none" : due < now ? "late" : romeDay(due) === today ? "today" : "next";
      const context: TodoItem["context"] = a.deal ? { label: a.deal.title, to: "/crm/deals/$dealId", params: { dealId: a.deal.id } } : a.contact ? { label: fullName(a.contact), to: "/crm/contacts/$id", params: { id: a.contact.id } } : a.company ? { label: a.company.name, to: "/crm/companies/$id", params: { id: a.company.id } } : undefined;
      return { key: `act-${a.id}`, bucket, day: due ? romeDay(due) : "", time: due ? fmtTime(due) : undefined, sort: due?.toISOString() ?? "", module: "crm", icon: ACT_ICON[a.type] ?? "check-list", kind: ACT_LABEL[a.type] ?? "Attività", title: a.title, link: context ?? { to: "/crm/activities" }, context, activityId: a.id };
    }),
  ];
  return { items, loading, showTasks, showActs, hasMine: !!(tasks.data?.length || acts.data?.length) };
}
